/**
 * Which required field paths belong to which wizard step (NC-1382).
 *
 * Spec section 9 promises "jeder Schritt erlaubt 'spaeter beantworten' (schreibt
 * nach `openItems`)". P1 (#1279) built the whole server side of that and left the
 * write path out, so `openItems` was read-only from the UI's point of view.
 *
 * Server-side contract this leans on, already shipped and tested:
 *   - `deriveProfileStatus()` counts a path listed in `openItems` as answered, so a
 *     deferred mandatory field no longer blocks `complete`
 *     (api/shared/__tests__/workloadProfileValidation.test.ts).
 *   - `assertKnownOpenItems()` rejects an unknown path with HTTP 400.
 *
 * `PROFILE_REQUIRED_PATHS` in api/shared/workloadProfileValidation.ts is the
 * authority for which paths exist. The frontend cannot import across the package
 * boundary (CRA forbids imports outside src/, and the api package has its own
 * tsconfig), so the step grouping below is a deliberate second copy. Two things
 * keep it honest rather than hoping it stays in sync:
 *
 *   1. `__tests__/profileStepFields.test.ts` walks every path against
 *      `emptyProfileInput()`, so a rename on the frontend type breaks the test.
 *   2. The server's 400 is the backstop for anything the type shape cannot catch.
 *
 * If you add a field to the profile, add it here too — the exhaustiveness test
 * names the count it expects and will tell you.
 */

import { WorkloadProfileInput } from '../../../services/types';

/**
 * Required paths per step. Index order MUST match STEP_LABELS in
 * WorkloadProfilePage: 0 Zweck, 1 Verantwortung, 2 Nutzung, 3 Voraussetzungen.
 *
 * Two paths are deliberately absent because they are not required (Spec section 4,
 * mirrored by PROFILE_REQUIRED_PATHS): `ownership.deputy` and `requirements.notes`.
 * Deferring a step must not mark an optional field as "bewusst offen" — that would
 * claim a decision nobody made.
 */
export const STEP_REQUIRED_PATHS: readonly (readonly string[])[] = [
  // 0 — Zweck
  ['purpose.goal', 'purpose.audience', 'purpose.audienceScope', 'purpose.alternative'],
  // 1 — Verantwortung (ownership.deputy is optional)
  ['ownership.contact', 'ownership.department', 'ownership.costCenterCode'],
  // 2 — Nutzung
  [
    'usage.frequency',
    'usage.participantsTypical',
    'usage.participantsMax',
    'usage.durationDays',
    'usage.runtimeProfile',
  ],
  // 3 — Voraussetzungen (requirements.notes is optional; acceptance renders here,
  //     see ProfileStepRequirements)
  [
    'requirements.licensing',
    'requirements.hasInputData',
    'requirements.outputTarget',
    'requirements.language',
    'acceptance.criterion',
  ],
];

/** Required paths of one step; empty for an index outside the wizard. */
export function stepRequiredPaths(step: number): readonly string[] {
  return STEP_REQUIRED_PATHS[step] ?? [];
}

/**
 * Is this step deferred? True only when EVERY required path of the step is listed.
 * A step where some paths were listed by another route counts as not deferred, so
 * toggling on completes the set rather than silently doing nothing.
 */
export function isStepDeferred(openItems: readonly string[], step: number): boolean {
  const paths = stepRequiredPaths(step);
  if (paths.length === 0) return false;
  return paths.every((path) => openItems.includes(path));
}

/**
 * Add or remove this step's required paths in `openItems`, leaving every other
 * entry untouched and in order — entries for other steps, and optional paths a
 * previous editor marked open, must survive.
 */
export function setStepDeferred(
  value: WorkloadProfileInput,
  step: number,
  deferred: boolean
): WorkloadProfileInput {
  const paths = stepRequiredPaths(step);
  if (paths.length === 0) return value;

  const openItems = deferred
    ? [...value.openItems, ...paths.filter((path) => !value.openItems.includes(path))]
    : value.openItems.filter((path) => !paths.includes(path));

  return { ...value, openItems };
}
